import { ChangeEvent, useCallback } from "react";

import { IOption } from "@evergis/ui";
import { AttributeOption } from "../../../../evergis/core/attributes/createAttributeOption";
import { actionsList } from "../components/CalculationTemplates/TemplateForm/TemplateFormActions";

import { useToggle } from "../../../../hooks/useToggle";
import { usePrintControls } from "./usePrintControls";

export type HandleSetExpressionParameter = (value: string | number) => void;

export const wrapToQuotes = (expression?: string) => {
  const operators = actionsList.map(({ text }) => text);
  const hasOperator = operators.some(operator => expression.includes(operator));

  if (expression && hasOperator) {
    return expression.replace(/['#]+/g, "");
  }

  if (expression && expression[0] === "#") {
    return `#'${expression.replace(/['#]+/g, "")}'`;
  }

  if (expression) {
    return `'${expression.replace(/[']+/g, "")}'`;
  }

  return "";
};

const OPERATORS_REGEX = /[+\-*/]/;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function parseInput(prevValue: string | number, inputValue: string, attributeOptions: AttributeOption[]) {
  let expression;
  const operators = actionsList.map(({ text }) => text);
  const attributeValues = attributeOptions.map(({ value }) => value);
  const lastInputCharacter = inputValue[inputValue.length - 1];
  let expressionOperands = inputValue.split(OPERATORS_REGEX);

  expressionOperands = expressionOperands.filter(string => !!string);

  const lastExpressionOperand = expressionOperands[expressionOperands.length - 1];

  if (
    (typeof prevValue === "string" && prevValue.length > inputValue.length) ||
    !Number.isNaN(+lastExpressionOperand) ||
    (attributeValues.includes(lastExpressionOperand) && operators.includes(lastInputCharacter))
  ) {
    expression = inputValue;
  } else {
    const wrappedNode = wrapToQuotes(lastExpressionOperand);

    expression = inputValue.replace(new RegExp(`${lastExpressionOperand}$`), wrappedNode);
  }

  return expression;
}

export const useAdvancedEditor = (
  handleSetExpression: HandleSetExpressionParameter,
  prevValue: string | number,
  attributeOptions: AttributeOption[],
  onChangeValue: (event: ChangeEvent<HTMLTextAreaElement>) => void,
) => {
  const [isvisibleAttributes, toggleAttributes] = useToggle();
  const [isvisibleDateFunctions, toggleDateFunctions] = useToggle();
  const { refElement, setExpression, printSymbol, printOperator, printNullValue, printDate } =
    usePrintControls(handleSetExpression);
  const selectOption = useCallback(
    (type: "dateFunctions" | "attributes") => (option: IOption) => {
      setExpression({ inputValue: option[0].value });

      if (type === "dateFunctions") {
        toggleDateFunctions();
      }

      if (type === "attributes") {
        toggleAttributes();
      }
    },
    [handleSetExpression, toggleAttributes, toggleDateFunctions],
  );

  const onChangeEditorValue = useCallback(
    (evt: ChangeEvent<HTMLTextAreaElement>) => {
      const inputValue = evt.target.value;

      handleSetExpression(inputValue);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      onChangeValue(inputValue);
    },
    [handleSetExpression, onChangeValue],
  );

  return {
    attributes: { isvisibleAttributes, toggleAttributes },
    dateFunctions: { isvisibleDateFunctions, toggleDateFunctions },
    refElement,
    handlers: {
      onChangeEditorValue,
      selectOption,
      printSymbol,
      printNullValue,
      printOperator,
      printDate,
    },
  };
};
